import React from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const CommunicationAnalytics = ({ analytics, currentLanguage }) => {
  const labels = {
    fr: {
      title: 'Analytiques de communication',
      totalSent: 'Total envoyé',
      deliveryRate: 'Taux de livraison',
      responseRate: 'Taux de réponse',
      avgResponseTime: 'Temps de réponse moyen',
      messagesByType: 'Messages par type',
      messagesByMonth: 'Messages par mois',
      hours: 'heures'
    },
    ar: {
      title: 'تحليلات الاتصالات',
      totalSent: 'إجمالي المرسل',
      deliveryRate: 'معدل التسليم',
      responseRate: 'معدل الاستجابة',
      avgResponseTime: 'متوسط وقت الاستجابة',
      messagesByType: 'الرسائل حسب النوع',
      messagesByMonth: 'الرسائل حسب الشهر',
      hours: 'ساعات'
    }
  };

  const monthlyData = [
    { month: currentLanguage === 'fr' ? 'Jan' : 'يناير', messages: 245 },
    { month: currentLanguage === 'fr' ? 'Fév' : 'فبراير', messages: 312 },
    { month: currentLanguage === 'fr' ? 'Mar' : 'مارس', messages: 289 },
    { month: currentLanguage === 'fr' ? 'Avr' : 'أبريل', messages: 356 },
    { month: currentLanguage === 'fr' ? 'Mai' : 'مايو', messages: 423 },
    { month: currentLanguage === 'fr' ? 'Juin' : 'يونيو', messages: 398 }
  ];

  const typeData = [
    { name: 'Email', value: 45, color: 'var(--color-primary)' },
    { name: 'WhatsApp', value: 35, color: 'var(--color-accent)' },
    { name: 'SMS', value: 20, color: 'var(--color-secondary)' }
  ];

  const stats = [
    {
      label: labels?.[currentLanguage]?.totalSent,
      value: analytics?.totalSent?.toLocaleString(),
      icon: 'Send',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      label: labels?.[currentLanguage]?.deliveryRate,
      value: `${analytics?.deliveryRate}%`,
      icon: 'CheckCheck',
      color: 'text-success',
      bgColor: 'bg-success/10'
    },
    {
      label: labels?.[currentLanguage]?.responseRate,
      value: `${analytics?.responseRate}%`,
      icon: 'MessageSquare',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    },
    {
      label: labels?.[currentLanguage]?.avgResponseTime,
      value: `${analytics?.avgResponseTime} ${labels?.[currentLanguage]?.hours}`,
      icon: 'Clock',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    }
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="bg-card rounded-lg border border-border shadow-warm p-4 md:p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="BarChart3" size={20} className="text-primary" />
          </div>
          <h2 className="text-lg md:text-xl font-semibold text-foreground">
            {labels?.[currentLanguage]?.title}
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {stats?.map((stat, index) => (
            <div
              key={index}
              className="border border-border rounded-lg p-4 hover:shadow-warm transition-smooth"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 rounded-lg ${stat?.bgColor} flex items-center justify-center`}>
                  <Icon name={stat?.icon} size={18} className={stat?.color} />
                </div>
                <span className="caption text-muted-foreground">{stat?.label}</span>
              </div>
              <p className="text-2xl font-semibold text-foreground">{stat?.value}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-foreground mb-4">
              {labels?.[currentLanguage]?.messagesByMonth}
            </h3>
            <div className="w-full h-64" aria-label="Monthly Messages Bar Chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis 
                    dataKey="month" 
                    stroke="var(--color-muted-foreground)"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis 
                    stroke="var(--color-muted-foreground)"
                    style={{ fontSize: '12px' }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'var(--color-popover)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="messages" fill="var(--color-primary)" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div>
            <h3 className="font-medium text-foreground mb-4">
              {labels?.[currentLanguage]?.messagesByType}
            </h3>
            <div className="w-full h-64" aria-label="Messages by Type Pie Chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={typeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {typeData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry?.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'var(--color-popover)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunicationAnalytics;